/**
 * Which teachers can actually take a class, and in what order to offer them.
 *
 * A teacher describes themselves on their own profile: the weekly windows they are free in,
 * the year levels they teach, and the subjects they can cover. Scheduling screens read that
 * here so the people who fit a booking come first.
 *
 * Nobody is ever removed. A teacher who has filled nothing in, or who is busy at that hour,
 * still appears and can still be picked — the office books around real life, and a list that
 * hid people would be worked around by not using the list. The ranking is advice, not a rule.
 */

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const weekdayLabel = (dayOfWeek) => WEEKDAY_LABELS[(Number(dayOfWeek) || 1) - 1] || '';

const minutes = (value) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || ''));
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/**
 * Does one declared window contain the whole booking?
 *
 * Containment, not overlap: being free from 16:00 to 17:00 is not being free for a class that
 * runs until 18:00, and offering that teacher first would send someone to fetch them for the
 * half of the lesson they cannot teach.
 */
const windowCovers = (window, need) => {
  if (Number(window.dayOfWeek) !== Number(need.dayOfWeek)) return false;
  const from = minutes(window.startTime);
  const to = minutes(window.endTime);
  const needFrom = minutes(need.startTime);
  const needTo = minutes(need.endTime);
  if ([from, to, needFrom, needTo].some(v => v === null)) return false;
  return from <= needFrom && to >= needTo;
};

/**
 * How well one teacher fits one booking.
 *
 * Each of the three answers is `true`, `false`, or `null` for "they never said". Unknown is
 * kept distinct from no on purpose: most teachers have not filled this in yet, and treating
 * silence as a refusal would bury the whole staff under the two people who have.
 *
 * @param {Object} teacher - availability / teachableGrades / teachableSubjectIds
 * @param {Object} need - dayOfWeek, startTime, endTime, and optionally year and subjectIds
 */
export const matchTeacher = (teacher = {}, need = {}) => {
  const availability = teacher.availability || [];
  const grades = (teacher.teachableGrades || []).map(String);
  const subjects = teacher.teachableSubjectIds || [];
  const wantedSubjects = (need.subjectIds || []).filter(Boolean);

  const canCheckTime = availability.length > 0
    && need.dayOfWeek && need.startTime && need.endTime;

  return {
    time: canCheckTime ? availability.some(w => windowCovers(w, need)) : null,
    grade: grades.length && need.year !== undefined && need.year !== null && need.year !== ''
      ? grades.includes(String(need.year))
      : null,
    subject: subjects.length && wantedSubjects.length
      ? wantedSubjects.every(id => subjects.includes(id))
      : null
  };
};

/**
 * The best fit across several bookings — how a whole course group is judged.
 *
 * Adding a teacher to a course group is not about one lesson: the group may run twice a week,
 * and being free for either is what makes the question "can this person take this class?"
 * answerable. So the bookings are scored separately and the best one wins.
 */
export const matchTeacherAny = (teacher = {}, needs = []) => {
  if (!needs.length) return matchTeacher(teacher, {});
  return needs
    .map(need => matchTeacher(teacher, need))
    .reduce((best, next) => (matchScore(next) > matchScore(best) ? next : best));
};

/** true / unknown / false, weighted so a stated no sinks below silence. */
const weigh = (value) => (value === true ? 2 : value === null ? 1 : 0);

export const matchScore = (match) => weigh(match.time) * 4 + weigh(match.grade) * 2 + weigh(match.subject);

/** A teacher worth putting at the top: nothing they said rules them out, and they are free. */
export const isRecommended = (match) =>
  match.time === true && match.grade !== false && match.subject !== false;

/**
 * Sort teachers best-fit first, then alphabetically.
 *
 * Alphabetical within a band matters as much as the ranking: the office knows its staff by
 * name, and a list that reshuffles on every booking is one nobody can scan.
 */
export const byBestMatch = (needs, nameOf = (t) => t.name || '') => {
  const list = Array.isArray(needs) ? needs : [needs];
  return (a, b) => {
    const diff = matchScore(matchTeacherAny(b, list)) - matchScore(matchTeacherAny(a, list));
    if (diff !== 0) return diff;
    return nameOf(a).localeCompare(nameOf(b));
  };
};

/**
 * The short note beside a name, or '' when there is nothing useful to say.
 *
 * Only ever states what the teacher themselves put on their profile. "Not free" means they
 * listed their hours and this is not one of them, never that no hours were listed.
 */
export const matchNote = (match, need = {}) => {
  const parts = [];
  if (match.time === true) parts.push('free');
  if (match.time === false) {
    parts.push(need.dayOfWeek ? `not free ${weekdayLabel(need.dayOfWeek)} ${need.startTime || ''}`.trim() : 'not free');
  }
  if (match.subject === false) parts.push('other subjects');
  if (match.grade === false) parts.push('other years');
  if (!parts.length && match.time === null) return 'no hours set';
  return parts.join(' · ');
};

/** Name plus note, for a plain <option> that cannot carry styling. */
export const teacherOptionLabel = (teacher, needs, name) => {
  const list = Array.isArray(needs) ? needs : [needs];
  const match = matchTeacherAny(teacher, list);
  const note = matchNote(match, list.length === 1 ? list[0] : {});
  return note ? `${name} — ${note}` : name;
};

/**
 * Whether any of these teachers has said anything for the ranking to work with.
 *
 * With nobody's profile filled in the order is just alphabetical and every note reads
 * "no hours set", so the screens use this to stay quiet rather than explain a feature that is
 * not doing anything yet.
 */
export const anyoneHasAvailability = (teachers = []) =>
  teachers.some(t => (t?.availability || []).length > 0);
